import React, { useState, useRef, useEffect } from 'react';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';

interface SlideTypeDropdownProps {
  selectedType: string;
  onTypeChange: (type: string) => void;
  slideTypes: string[];
}

const SlideTypeDropdown: React.FC<SlideTypeDropdownProps> = ({
  selectedType,
  onTypeChange,
  slideTypes
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setFocusedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          setFocusedIndex(slideTypes.indexOf(selectedType));
        } else if (focusedIndex >= 0) {
          handleSelect(slideTypes[focusedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setFocusedIndex(-1);
        buttonRef.current?.focus();
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          setFocusedIndex(slideTypes.indexOf(selectedType));
        } else {
          setFocusedIndex(prev => 
            prev < slideTypes.length - 1 ? prev + 1 : 0
          );
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          setFocusedIndex(slideTypes.indexOf(selectedType));
        } else {
          setFocusedIndex(prev => 
            prev > 0 ? prev - 1 : slideTypes.length - 1
          );
        }
        break;
      case 'Home':
        event.preventDefault();
        if (isOpen) {
          setFocusedIndex(0);
        }
        break;
      case 'End':
        event.preventDefault();
        if (isOpen) {
          setFocusedIndex(slideTypes.length - 1);
        }
        break;
    }
  };

  const handleSelect = (type: string) => {
    onTypeChange(type);
    setIsOpen(false);
    setFocusedIndex(-1);
    buttonRef.current?.focus();
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setFocusedIndex(slideTypes.indexOf(selectedType));
    } else {
      setFocusedIndex(-1);
    }
  };

  return (
    <div className="relative w-full max-w-xs mb-4" ref={dropdownRef}>
      <label 
        htmlFor="slide-type-dropdown" 
        className="block text-base font-semibold text-slate-700 mb-2 mt-6"
      >
        Slide Type
      </label>
      
      <button
        ref={buttonRef}
        id="slide-type-dropdown"
        type="button"
        className="relative w-full bg-white border border-slate-300 rounded-md shadow-sm pl-3 pr-10 py-2 text-left cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-slate-400 transition-colors duration-200"
        onClick={toggleDropdown}
        onKeyDown={handleKeyDown}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-labelledby="slide-type-dropdown-label"
      >
        <span className="block truncate text-slate-900 font-medium">
          {selectedType}
        </span>
        <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
          {isOpen ? (
            <FaChevronUp className="h-4 w-4 text-slate-400" aria-hidden="true" />
          ) : (
            <FaChevronDown className="h-4 w-4 text-slate-400" aria-hidden="true" />
          )}
        </span>
      </button>

      {isOpen && (
        <ul
          ref={listRef}
          className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none border border-slate-200"
          role="listbox"
          aria-labelledby="slide-type-dropdown"
        >
          {slideTypes.map((type, index) => (
            <li
              key={type}
              className={`cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-slate-50 transition-colors duration-150 ${
                focusedIndex === index ? 'bg-blue-50 text-blue-900' : 'text-slate-900'
              } ${
                selectedType === type ? 'bg-blue-100 font-semibold' : 'font-normal'
              }`}
              onClick={() => handleSelect(type)}
              role="option"
              aria-selected={selectedType === type}
              onMouseEnter={() => setFocusedIndex(index)}
            >
              <span className="block truncate">
                {type}
              </span>
              {selectedType === type && (
                <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-blue-600">
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SlideTypeDropdown;